import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Calendar, Clock, ArrowUpRight, BookOpen, Loader2 } from "lucide-react";
import { api } from "@/lib/api";

export default function Blog() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    document.title = "Blog — T-Permit, Finance & Taxi Business Guides | Be Easy Cars";
    api.get("/blog/posts").then(({ data }) => setPosts(data)).finally(() => setLoading(false));
  }, []);

  return (
    <div data-testid="blog-page">
      <section className="relative pt-32 pb-12 md:pt-40 md:pb-16 overflow-hidden">
        <div className="absolute inset-0 grid-bg opacity-60" />
        <div className="absolute -top-40 left-1/2 -translate-x-1/2 w-[900px] h-[500px] radial-glow-red pointer-events-none" />
        <div className="relative max-w-6xl mx-auto px-6 lg:px-10">
          <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.28em] text-red-500 mb-4 font-mono-brand">
            <BookOpen className="w-4 h-4" /> Insights
          </div>
          <h1 className="font-display text-5xl md:text-6xl lg:text-7xl font-black tracking-tighter leading-[1.05] text-white">
            Guides for <span className="text-gradient-red">Taxi Entrepreneurs</span>
          </h1>
          <p className="mt-6 text-lg text-slate-400 max-w-3xl leading-relaxed">
            Honest, practical articles on T-Permit registration, commercial vehicle finance,
            CNG vs EV, airport attachment and how to run a profitable taxi business.
          </p>
        </div>
      </section>

      <section className="py-16">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          {loading ? (
            <div className="min-h-[40vh] flex items-center justify-center text-slate-400">
              <Loader2 className="w-6 h-6 animate-spin" />
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {posts.map((p, i) => (
                <motion.article
                  key={p.slug}
                  initial={{ opacity: 0, y: 24 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: i * 0.08 }}
                  className="group glass glass-hover rounded-3xl overflow-hidden flex flex-col"
                  data-testid={`blog-card-${p.slug}`}
                >
                  <Link to={`/blog/${p.slug}`} className="block relative aspect-[16/9] overflow-hidden">
                    <img
                      src={p.cover}
                      alt={p.title}
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                      loading="lazy"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
                    <div className="absolute bottom-3 left-3 flex flex-wrap gap-2">
                      {p.tags.slice(0, 2).map((t) => (
                        <span key={t} className="text-[10px] uppercase tracking-widest text-white bg-red-600/70 backdrop-blur px-2 py-1 rounded-full">
                          {t}
                        </span>
                      ))}
                    </div>
                  </Link>
                  <div className="p-6 flex-1 flex flex-col">
                    <div className="flex items-center gap-3 text-[10px] uppercase tracking-widest text-slate-500 font-mono-brand mb-3">
                      <span className="flex items-center gap-1"><Calendar className="w-3 h-3" /> {p.published_at}</span>
                      <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> {p.read_minutes} min</span>
                    </div>
                    <h3 className="font-display font-bold text-xl text-white leading-tight group-hover:text-red-400 transition-colors">
                      <Link to={`/blog/${p.slug}`}>{p.title}</Link>
                    </h3>
                    <p className="mt-3 text-sm text-slate-400 leading-relaxed line-clamp-3">{p.excerpt}</p>
                    <div className="mt-auto pt-5">
                      <Link
                        to={`/blog/${p.slug}`}
                        className="inline-flex items-center gap-1.5 text-sm text-red-400 hover:text-red-300 font-semibold"
                        data-testid={`blog-read-${p.slug}`}
                      >
                        Read article <ArrowUpRight className="w-4 h-4" />
                      </Link>
                    </div>
                  </div>
                </motion.article>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
